import {PurchaseOrder, PurchaseOrderListRow} from './_models'
import {formatExchangeRateDisplay} from './currencyConversion'

const normalizeCurrency = (value: unknown) => String(value ?? '').trim().toUpperCase()

const isCustomCode = (value: string) => value === 'CUSTOM' || value === 'OTHER'

export const resolvePurchaseOrderOriginalCurrency = (po: PurchaseOrder | PurchaseOrderListRow): string => {
  const quotationCurrency = normalizeCurrency(po?.quotationCurrency)
  const currency = normalizeCurrency(po?.currency)
  const customCurrency = normalizeCurrency((po as any)?.customCurrency)
  if (quotationCurrency && !isCustomCode(quotationCurrency)) return quotationCurrency
  if (currency && !isCustomCode(currency)) return currency
  if (customCurrency) return customCurrency
  if (quotationCurrency) return quotationCurrency
  if (currency) return currency
  return 'USD'
}

const readFinite = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

export type PurchaseOrderMoneyPresentation = {
  originalCurrency: string
  originalAmount: number | null
  companyCurrency: string | null
  companyAmount: number | null
  sameCurrency: boolean
  hasCompanySnapshot: boolean
  displayExchangeRate: number | null
  exchangeRateDisplay: string | null
  primaryAmount: number | null
  primaryCurrency: string | null
  secondaryAmount: number | null
  secondaryCurrency: string | null
  auxiliaryText: string | null
}

export const resolvePurchaseOrderMoneyPresentation = (
  po: PurchaseOrder | PurchaseOrderListRow,
  vendorView = false
): PurchaseOrderMoneyPresentation => {
  const originalCurrency = resolvePurchaseOrderOriginalCurrency(po)
  const originalAmount = readFinite((po as any)?.originalTotal ?? po?.totalAmount)
  const rawCompanyCurrency = normalizeCurrency((po as any)?.companyCurrencyCode) || null
  const sameCurrencyByCode = rawCompanyCurrency != null && rawCompanyCurrency === originalCurrency
  const storedCompanyAmount = readFinite((po as any)?.companyTotal)
  const companyAmount = storedCompanyAmount ?? (sameCurrencyByCode ? originalAmount : null)
  const companyCurrency = rawCompanyCurrency
  const hasCompanySnapshot = companyCurrency != null && companyAmount != null
  const sameCurrency = companyCurrency != null && companyCurrency === originalCurrency
  const resolvedDisplayExchangeRate =
    companyCurrency != null
      ? sameCurrency
        ? 1
        : hasCompanySnapshot
          ? readFinite((po as any)?.exchangeRate)
          : null
      : null
  const storedExchangeRateDisplay = String((po as any)?.exchangeRateDisplay ?? '').trim() || null
  const computedExchangeRateDisplay =
    companyCurrency != null && (sameCurrency || hasCompanySnapshot)
      ? formatExchangeRateDisplay(originalCurrency, resolvedDisplayExchangeRate, companyCurrency)
      : null
  const exchangeRateDisplay =
    computedExchangeRateDisplay || (hasCompanySnapshot ? storedExchangeRateDisplay : null)

  if (vendorView) {
    return {
      originalCurrency,
      originalAmount,
      companyCurrency,
      companyAmount,
      sameCurrency,
      hasCompanySnapshot,
      displayExchangeRate: resolvedDisplayExchangeRate,
      exchangeRateDisplay,
      primaryAmount: originalAmount,
      primaryCurrency: originalCurrency,
      secondaryAmount: hasCompanySnapshot && !sameCurrency ? companyAmount : null,
      secondaryCurrency: hasCompanySnapshot && !sameCurrency ? companyCurrency : null,
      auxiliaryText: hasCompanySnapshot && !sameCurrency ? exchangeRateDisplay : null,
    }
  }

  if (hasCompanySnapshot) {
    return {
      originalCurrency,
      originalAmount,
      companyCurrency,
      companyAmount,
      sameCurrency,
      hasCompanySnapshot,
      displayExchangeRate: resolvedDisplayExchangeRate,
      exchangeRateDisplay,
      primaryAmount: companyAmount,
      primaryCurrency: companyCurrency,
      secondaryAmount: sameCurrency ? null : originalAmount,
      secondaryCurrency: sameCurrency ? null : originalCurrency,
      auxiliaryText: sameCurrency ? null : exchangeRateDisplay,
    }
  }

  return {
    originalCurrency,
    originalAmount,
    companyCurrency,
    companyAmount,
    sameCurrency,
    hasCompanySnapshot,
    displayExchangeRate: resolvedDisplayExchangeRate,
    exchangeRateDisplay,
    primaryAmount: companyCurrency && !sameCurrency ? null : originalAmount,
    primaryCurrency: companyCurrency || originalCurrency,
    secondaryAmount: null,
    secondaryCurrency: null,
    auxiliaryText: vendorView ? null : 'Company currency conversion unavailable',
  }
}
